/**
 * OTA-FS-2D — helpers puros para a UI de OTA-FS (comando cloud `fs_update` via
 * POST /api/admin/devices/{id}/fs-ota). Sem React, sem I/O → testável em node:
 *   node frontend/test/fs_ota.test.mjs
 *
 * Contexto:
 *   - O firmware OTA-FS-2C reporta no heartbeat `fs` (fs_version) e `fs_ota=true`
 *     (capability real: sabe EXECUTAR fs_update, não só reportar fs).
 *   - O backend /fs-ota exige release kind=fs + secure_channel; bloqueia device
 *     protegido (403) e device sem suporte (412).
 */

/**
 * Estado de capability do device para OTA-FS, a partir do heartbeat cru (hb =
 * device.last_hb_payload). NÃO decide sozinho o gate final — o componente ainda
 * combina com secure_channel e proteção. Preferimos `fs_ota=true`; se só houver
 * `fs_version`, o suporte é INCERTO (firmware OTA-FS-1 reporta fs mas não executa).
 *
 * @returns {{level: "supported"|"uncertain"|"unsupported", enabled: boolean,
 *            fsVersion: (string|null), label: string}}
 */
export function fsCapabilityState(hb) {
  const h = hb || {};
  const hasFsOta = h.fs_ota === true;
  const fsVer = typeof h.fs === "string" && h.fs.length > 0 ? h.fs : null;
  const hasFsVersion = !!fsVer && fsVer !== "unknown";

  if (hasFsOta) {
    return {
      level: "supported",
      enabled: true,
      fsVersion: fsVer,
      label: "Firmware suporta OTA-FS (fs_ota=true).",
    };
  }
  if (hasFsVersion) {
    return {
      level: "uncertain",
      enabled: false,
      fsVersion: fsVer,
      label:
        "Compatibilidade incerta: o device reporta fs_version mas ainda não " +
        "reporta fs_ota. Atualize o firmware (OTA-FS-2C) antes de usar OTA-FS.",
    };
  }
  return {
    level: "unsupported",
    enabled: false,
    fsVersion: null,
    label:
      "Este Sonoff ainda não reporta suporte a OTA-FS. Atualize o firmware primeiro.",
  };
}

/**
 * Mensagem amigável de erro do /fs-ota por status HTTP. `err` = ApiError
 * ({ status, message, body }).
 */
export function fsOtaErrorMessage(err) {
  const status = err?.status;
  const detail = err?.message || err?.body?.detail || "";
  switch (status) {
    case 400:
      // 400 = release kind cruzada (app no /fs-ota) ou payload inválido.
      return /kind|filesystem|littlefs/i.test(detail)
        ? "Release inválida: para OTA-FS use um arquivo LittleFS (kind=fs), não firmware app."
        : detail || "Requisição inválida para OTA-FS.";
    case 403:
      return "Device protegido. Operação bloqueada pelo NOC.";
    case 404:
      return "Device ou release não encontrado.";
    case 409:
      return "Já existe uma operação em andamento para este device.";
    case 412:
      return "Firmware do device não suporta fs_update/fs_ota. Atualize o firmware primeiro.";
    case 422:
      return "Dados inválidos para o disparo de OTA-FS.";
    default:
      return detail || "Falha ao disparar OTA-FS.";
  }
}

/** true se a release é de filesystem (LittleFS). Default do backend é kind="app". */
export function isFsRelease(r) {
  return (r?.kind || "app") === "fs";
}

/** Heurística leve: o nome do arquivo parece um `.bin`? (validação visual do upload). */
export function looksLikeBin(name) {
  return typeof name === "string" && /\.bin$/i.test(name.trim());
}

/** Rótulo/tom do badge de tipo de release (app vs fs). */
export function releaseKindBadge(r) {
  return isFsRelease(r)
    ? { label: "FS", title: "Filesystem / LittleFS (kind=fs)", tone: "fs" }
    : { label: "APP", title: "Firmware do app (kind=app)", tone: "app" };
}
